// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: bd4f8ff3-7717-439e-9eee-66449a0fd468

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  AppBar, Toolbar, Typography, Button, Box, Select, MenuItem, Chip, Stack,
  Menu, Divider, IconButton, useMediaQuery,
  Tooltip, Alert,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Inventory from './Inventory';
import Dashboard from './Dashboard';
import FactSheetDetail from './FactSheetDetail';
import MasterCatalog from './MasterCatalog';
import LandscapeReport from './LandscapeReport';
import CapabilityMap from './CapabilityMap';
import MatrixReport from './MatrixReport';
import ReadinessView from './ReadinessView';
import InterfaceHub from './InterfaceHub';
import CostModel from './CostModel';
import SurveyWorkflow from './SurveyWorkflow';
import FileExchange from './FileExchange';
import SavedViews from './SavedViews';
import FormsRoundtrip from './FormsRoundtrip';
import SnapshotCompare from './SnapshotCompare';
import Benchmarks from './Benchmarks';
import WavePlanner from './WavePlanner';
import DocumentExtract from './DocumentExtract';
import InfrastructureImport from './InfrastructureImport';
import RelationshipWizard from './RelationshipWizard';
import CsgTargetState from './CsgTargetState';
import ServerMapping from './ServerMapping';
import Rightsizing from './Rightsizing';
import TemplateLibrary from './TemplateLibrary';
import RelationMap from './RelationMap';
import CatalogIntelligence from './CatalogIntelligence';
import PortfolioReport from './PortfolioReport';
import Obsolescence from './Obsolescence';
import ReportsGallery from './ReportsGallery';
import DataFlowDiagram from './DataFlowDiagram';
import NamingDictionary from './NamingDictionary';
import TargetArchitecture from './TargetArchitecture';
import MeetingIntelligence from './MeetingIntelligence';
import Roadmap from './Roadmap';
import AiEnrichment from './AiEnrichment';
import RoadmapView from './RoadmapView';
import MindMap from './MindMap';
import ListsAdmin from './ListsAdmin';
import CustomerAdmin from './CustomerAdmin';
import ContractsRenewals from './ContractsRenewals';
import FinOps from './FinOps';
import CloudDiscovery from './CloudDiscovery';
import MasterDashboard from './MasterDashboard';
import MyDashboard from './MyDashboard';
import ReplayIcon from '@mui/icons-material/Replay';
import HelpBubble from './HelpBubble';
import Tour from './Tour';
import { requestTourReplay } from './tourState';
import { tourStepsForSession } from './tourSteps';
import { shouldAutoStart, onTourReplayRequested } from './tourState';
import BulkTools from './BulkTools';
import { ThemeModeToggle } from './ThemeProvider';
import UserAdmin from './UserAdmin';
import PrimordiaLogo from './PrimordiaLogo';
import Login from './Login';
import store, { initStore } from './store';
import { loadConfig, getConfig } from './config';
import { getSession, signOut, mfaStatus } from './auth';
import ChangePassword from './ChangePassword';
import AuthenticatorSettings from './AuthenticatorSettings';
import AdminPortal from './AdminPortal';
import Feedback from './Feedback';
import HelpCenter from './HelpCenter';

// Self-contained CMDB app. Internal state-based navigation keeps it decoupled
// from the legacy app's router/auth so it renders anywhere we mount it.
const REPORT_VIEWS = ['gallery', 'landscape', 'capabilitymap', 'matrix', 'roadmap', 'mindmap', 'relationmap', 'portfolio', 'obsolescence', 'dataflow', 'targetarch', 'readiness', 'interfaces', 'cost', 'views', 'snapshots', 'benchmarks', 'waveplanner'];
// Nav buttons live on the blue AppBar: white text, and the active one becomes
// a white pill with navy text (the default contained-primary is blue-on-blue).
const NAV_BTN = {
  // Inherit the AppBar's own per-mode ink (navy on the white light-mode bar,
  // light text on the dark bar) — scoped to the text variant so the active
  // contained pill keeps MUI's palette-driven primary look in both modes.
  // NEVER hardcode ink here: white text was invisible on the light bar, and
  // the navy it replaced was invisible on the dark one.
  '&.MuiButton-text': { color: 'inherit' },
};

// Membership drives ONLY which top-level button renders as active. It is not
// the menu -- the menu is the JSX below -- which is precisely how these two
// drifted: FINOPS_VIEWS was split out and the four screens were removed from
// the Manage MENU, but they were left in this list. The visible symptom was
// two nav buttons lit at once on every FinOps screen, which reads as "you are
// in two places", and is the kind of thing nobody files a bug about.
//
// users/lists/customers are NOT in the menu either, and are correct to be
// here: Administration folds them into one screen, so they are sub-views
// reached from inside 'admin' and the Manage button should stay lit on them.
const MANAGE_VIEWS = ['admin', 'users', 'lists', 'customers', 'surveys', 'files', 'forms', 'ai', 'extract',
  'relwizard', 'csg', 'meetings', 'templates', 'contracts'];
// The Explore group's members, so the collapsed button can show as active the
// same way Manage does. Kept beside MANAGE_VIEWS rather than inlined: two
// lists that must agree about which screen lives where is exactly the drift
// this file already pays for elsewhere.
const EXPLORE_VIEWS = ['inventory', 'deployments', 'allrecords', 'dashboard', 'mydash', 'whatsnew', 'help'];
// FinOps is its own group rather than living under Servers, and the reason is
// that it SPANS both inventories: showback attributes spend to APPLICATIONS,
// discovery finds SERVERS, cost modelling models workloads. Nested under either
// one it would be in the wrong place half the time. Applications and Servers
// are also lists, not containers for tools.
//
// These four were scattered across two menus -- three under Manage and cost
// modelling under Reports -- which is what made the case: related screens
// filed by the mechanism that built them rather than the job they do.
const FINOPS_VIEWS = ['finops', 'clouddiscovery', 'rightsizing', 'cost'];

// Servers is its own group as of 2026-08-19. Frank: "should naming dictionary
// be server specific, not under manage?" It should, and so should the other
// two: naming conventions, server-to-deployment mapping and infrastructure
// import are all tools that act on the SERVER inventory, and they were filed
// under Manage because that is where admin-shaped screens went, not because
// anyone thought a person looking for them would start there.
//
// Manage was seventeen items deep. Three of them belonged to a noun the
// product already has a nav entry for.
//
// Same argument that moved the FinOps four out of Manage, and it lands the
// same way: a screen filed by the mechanism that built it rather than by the
// job it does is a screen nobody finds.
const SERVER_VIEWS = ['servers', 'naming', 'servermap', 'infra'];

// Master is a top-level group as of 2026-08-20. Frank: "I think Master needs to
// be its own upper tab, not under Manage", and "the cross-client should be
// under that one as well."
//
// The reason it was under Manage is that it is administrative, which confuses
// two different senses of the word. Everything else in Manage acts on ONE
// client's estate. These two act on the shared body of knowledge that spans
// every engagement -- a different subject, not a different permission. Burying
// the catalogue three items below "Documents" also made the product's only
// cross-client asset look like a filing cabinet.
const MASTER_VIEWS = ['catalog', 'catalogintel'];

// Screens that need a CLIENT ESTATE to mean anything: discovery, mapping,
// import, the wizards, contracts, templates. The master workspace holds no
// fact sheets of its own, so all of these were offered in a context where they
// have nothing to act on. Relationship wizard, CSG and server-to-deployment
// mapping were the ones Frank hit; the rest are the same mistake, found by
// checking the whole class rather than only what was reported.
//
// One named list rather than the same conditional at a dozen call sites,
// because a dozen copies of a rule is a dozen chances for the thirteenth
// screen to forget it.
//
// 'servers' and 'inventory' were added 2026-08-19. Frank, on prod: "if you
// click on explore and go to servers there are servers in the list and if we
// are in master they should not be, they should only be in the respective
// client." He is right, and the cause is one line in CLAUDE.md:
// listFactSheets('optimum', ...) returns EVERY tenant's rows. So the master
// workspace was not showing a shared list, it was showing every client's
// servers blended into one, with nothing on screen saying whose each row was.
//
// The same is true of Applications, and that one has already cost a
// conversation: master's Applications screen showed business owners and it
// looked like the master catalogue had them, when it was blended client fact
// sheets all along. Both are gated.
//
// Master keeps the two views that are cross-client ON PURPOSE and say so:
// "All client records", and the Master catalogue, which holds products rather
// than anybody's deployments.
const CLIENT_ONLY_VIEWS = [
  'relwizard', 'csg', 'infra', 'extract', 'servermap', 'naming',
  'surveys', 'forms', 'contracts', 'ai', 'templates',
  'servers', 'inventory', 'deployments',
];
const isClientOnly = (v) => CLIENT_ONLY_VIEWS.includes(v);

// A nav entry with its own "?" beside it. The bubble stops propagation so
// opening the explanation does not also navigate away from the menu you are
// reading it in -- which is the whole failure mode of putting help inside a
// menu item.
function NavItem({ viewKey, label, selected, onPick }) {
  return (
    <MenuItem selected={selected} onClick={() => onPick(viewKey)}
      sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <span>{label}</span>
      <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
        <HelpBubble topic={viewKey} />
      </span>
    </MenuItem>
  );
}

export default function CmdbApp() {
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('optimum');
  const [view, setViewRaw] = useState('inventory'); // inventory | dashboard | factsheet
  const [selectedId, setSelectedIdRaw] = useState(null);

  // The browser Back button used to leave the PRODUCT — back to whatever site
  // preceded it — because every screen change here was plain React state and
  // never touched session history. In a single-page app that is not a cosmetic
  // annoyance: someone three screens deep who hits Back loses the whole session
  // context, and Back is the most-pressed control in a browser.
  //
  // It is fixed here, in ONE place, rather than at the ~20 setView call sites:
  // setView is wrapped, so every existing caller (nav buttons, menus, deep
  // links, the fact-sheet open path) gets history for free and a new screen
  // added later cannot forget to opt in. Refs mirror the state because a push
  // has to record the values as they are AT THE MOMENT of navigation, and the
  // useState values in a closure are a render behind — open() sets the id and
  // then the view in the same tick, so only a ref sees the id that belongs
  // with the view being pushed.
  const viewRef = useRef('inventory');
  const selectedIdRef = useRef(null);

  const setSelectedId = useCallback((id) => {
    selectedIdRef.current = id;
    setSelectedIdRaw(id);
  }, []);

  const setView = useCallback((next) => {
    if (next === viewRef.current) return;   // re-clicking the current tab is not navigation
    viewRef.current = next;
    window.history.pushState({ primordia: { view: next, selectedId: selectedIdRef.current } }, '');
    setViewRaw(next);
  }, []);

  useEffect(() => {
    // The FIRST entry has to carry our state too. Without this, Back from the
    // very first navigation lands on an entry we did not write, the handler
    // below correctly declines to act on it, and the user is dropped out of the
    // app again — the exact bug, surviving in the one case nobody tests.
    if (!window.history.state || !window.history.state.primordia) {
      window.history.replaceState(
        { primordia: { view: viewRef.current, selectedId: selectedIdRef.current } }, '');
    }
    const onPop = (e) => {
      const st = (e.state && e.state.primordia) || null;
      // Not ours (a hash change, or a page before this app) — leave the browser
      // alone rather than swallowing a navigation we do not own.
      if (!st) return;
      viewRef.current = st.view;
      selectedIdRef.current = st.selectedId == null ? null : st.selectedId;
      setViewRaw(st.view);
      setSelectedIdRaw(selectedIdRef.current);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [booting, setBooting] = useState(true);
  const [loadedTenants, setLoadedTenants] = useState([]);
  const [feedbackKind, setFeedbackKind] = useState(null);
  // MFA gate. `null` = not checked yet, so nothing is asserted before Cognito
  // has answered — a slow check must never flash a demand at someone who is
  // already enrolled.
  const [mfaRequired, setMfaRequired] = useState(false);

  const [session, setSession] = useState(null);
  // "What's coming" was gated on the WORKSPACE being the master one, so an
  // Optimum person lost the screen the moment they switched into a client's
  // portal — which is most of their day. Being Optimum staff is a property of
  // the PERSON, not of the workspace they happen to be standing in. Clients
  // still never see it.
  const isOptimumStaff = !!session && (session.groups || []).includes('OptimumAdmins');

  // An account that can read across EVERY client is not allowed to sit behind a
  // password alone. This is the whole reason the pool cannot simply be flipped
  // to MfaConfiguration:ON — under ON, Cognito challenges for a factor it still
  // holds even after an admin "resets" it, and there is no admin un-enroll API,
  // so the pool-level switch turns a lost phone into a lockout with no remedy.
  // Gating in the app gets the same practical outcome and stays reversible.
  //
  // Deliberately master-only for now: a client user losing access to their own
  // workspace is a support call, but an Optimum account is a key to every
  // client's estate at once.
  useEffect(() => {
    let alive = true;
    if (!session || !isOptimumStaff) { setMfaRequired(false); return undefined; }
    if (session.sso) { setMfaRequired(false); return undefined; }   // factor lives with their IdP
    mfaStatus().then((st) => {
      // enrolled === null means the check itself failed; do not lock someone out
      // of the product because Cognito was briefly unreachable.
      if (alive) setMfaRequired(st && st.enrolled === false);
    }).catch(() => {});
    return () => { alive = false; };
  }, [session, isOptimumStaff]);
  const canSeeRoadmap = isOptimumStaff || !getConfig().authRequired;
  const [exploreAnchor, setExploreAnchor] = useState(null);
  const [finopsAnchor, setFinopsAnchor] = useState(null);
  const [serversAnchor, setServersAnchor] = useState(null);
  const [masterAnchor, setMasterAnchor] = useState(null);
  const [reportsAnchor, setReportsAnchor] = useState(null);
  const [manageAnchor, setManageAnchor] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [authAppOpen, setAuthAppOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [helpTopic, setHelpTopic] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [loadingTenant, setLoadingTenant] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  // The persistence layer says so once per session when a workspace is too
  // large to cache offline — the API stays the source of truth, so this is
  // "not cached", never "data lost". See store.persistenceDegraded().
  const [storageWarning, setStorageWarning] = useState(null);
  // Below md the toolbar cannot hold the menus, the tenant switcher and the
  // account block at once, so navigation moves into a drawer.
  const theme = useTheme();

  // The top bar carries brand + optional client logo + seven nav controls + the
  // workspace switcher + help/feedback/account. MEASURED on a real render: the
  // nav block alone is 756px, and a signed-in master inside a client workspace
  // needs ~1640px once that client's logo, the tenant name and the account
  // email are in it.
  //
  // It used to collapse only below 'md' (900px), so every laptop between 900 and
  // ~1600 drew the whole row inline with nothing allowed to shrink, and the
  // switcher hung off the right edge — invisible, because the bar does not
  // scroll and nothing reported the clipping.
  //
  // Collapse below 'xl' (1536) instead. That is deliberately generous: the width
  // actually needed depends on how long THIS client's name is and whether they
  // have a logo, so the threshold is set for the worst case rather than the
  // average one. The cost is a hamburger on a 1440 laptop where the nav might
  // just have fitted; the alternative is a control nobody can reach on someone
  // else's screen, which is how this was found.
  //
  // A measured/ResizeObserver version was tried and rejected: once collapsed the
  // nav cannot be measured, so re-expanding needs a cached width, and getting
  // that wrong fails OPEN — the bar silently clips again. Deterministic wins.
  // 1400 rather than the xl token: xl is 1536px, which is WIDER than a
  // maximised MacBook, so every laptop was handed the collapsed drawer and lost
  // the top bar entirely. That is not what making it responsive was meant to do.
  const compact = useMediaQuery(theme.breakpoints.down(1400));

  // Boot: load runtime config -> require auth when deployed -> hydrate the store.
  useEffect(() => {
    (async () => {
      await loadConfig();
      const s = getSession();
      setSession(s);
      if (!getConfig().authRequired || s) {
        // A master lands in 'optimum', which holds no records, so load a
        // workspace that does — otherwise the first screen is empty and reads
        // as a broken app rather than as an empty master view.
        const landing = (s && s.tenantId && !(s.groups || []).includes('OptimumAdmins'))
          ? s.tenantId : 'demo';
        await initStore({ tenantId: landing });
        const all = await store.listTenants();
        // Only a master may see the client roster. For a client user the switcher
        // must contain exactly their own tenant — otherwise the list of Optimum's
        // clients leaks, and on the offline fallback path they could switch into one.
        const isMaster = !!s && (s.groups || []).includes('OptimumAdmins');
        setTenants(isMaster || !getConfig().authRequired ? all : all.filter((t) => t.id === s.tenantId));
        // A client user only ever lands in their own portal.
        if (s && s.tenantId && !(s.groups || []).includes('OptimumAdmins')) setTenantId(s.tenantId);
        // A master lands on the cross-client overview, not on optimum's (empty)
        // inventory — the blank first screen was reported as "no data" twice.
        if (isMaster) setView('dashboard');
      }
      store.loadedTenantIds().then(setLoadedTenants).catch(() => setLoadedTenants([]));
      setBooting(false);
    })();
  }, []);

  // HelpBubble ("Open in wiki") has no idea how this app navigates, so it
  // fires a CustomEvent instead of taking a callback prop — this is the one
  // place that turns that event into a real view switch. Also honours a
  // bookmarked or refreshed `#help=<topic>` link on first load, since a hash
  // is the only part of that link this state-based app (no router) can read.
  useEffect(() => {
    const onOpenHelp = (e) => {
      setHelpTopic((e.detail && e.detail.topic) || null);
      setView('help');
    };
    window.addEventListener('primordia:open-help', onOpenHelp);
    if (/help=/.test(window.location.hash || '')) {
      const m = /help=([^&]+)/.exec(window.location.hash);
      setHelpTopic(m ? decodeURIComponent(m[1]) : null);
      setView('help');
    }
    return () => window.removeEventListener('primordia:open-help', onOpenHelp);
  }, []);

  // First-run walkthrough: auto-start once per browser after boot settles, and
  // honour Help Center's "Replay the tour" event any time after that.
  useEffect(() => {
    if (booting) return;
    if (shouldAutoStart(session)) setTourOpen(true);
  }, [booting, session]);
  useEffect(() => onTourReplayRequested(() => setTourOpen(true)), []);

  // Surface degraded offline-caching once the store reports it (writes happen
  // after mount, hence the poll).
  useEffect(() => {
    const check = () => {
      const state = store.persistenceDegraded();
      setStorageWarning(state.degraded ? state.reason : null);
    };
    check();
    const id = setInterval(check, 5000);
    return () => clearInterval(id);
  }, []);

  const onSignedIn = async (s) => {
    setSession(s);
    setBooting(true);
    const landing2 = (s.tenantId && !(s.groups || []).includes('OptimumAdmins')) ? s.tenantId : 'demo';
    if ((s.groups || []).includes('OptimumAdmins')) setView('dashboard');
    await initStore({ tenantId: landing2 });
    const all2 = await store.listTenants();
    const master2 = (s.groups || []).includes('OptimumAdmins');
    setTenants(master2 ? all2 : all2.filter((t) => t.id === s.tenantId));
    if (s.tenantId && !(s.groups || []).includes('OptimumAdmins')) setTenantId(s.tenantId);
    store.loadedTenantIds().then(setLoadedTenants).catch(() => setLoadedTenants([]));
    setBooting(false);
  };

  // `loadingTenant` covers a workspace SWITCH, which fetches. Without it the
  // previous workspace's records stay on screen under the new workspace's name
  // — a master would read one client's estate as another's.
  if (booting || loadingTenant) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Stack alignItems="center" spacing={2}>
          <PrimordiaLogo size={64} spinning />
          <Typography sx={{ fontSize: 11, color: 'text.secondary', letterSpacing: 1.5 }}>
            {loadingTenant ? 'LOADING WORKSPACE' : 'LOADING'}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Gate: when deployed, nothing renders without a valid Cognito session.
  if (getConfig().authRequired && !session) {
    return <Login onSignedIn={onSignedIn} />;
  }

  // Each workspace is fetched on demand. Nothing is preloaded, because loading
  // every client at once is what exceeded the response limit and silently
  // dropped the app onto seed data.
  const switchTenant = async (id) => {
    setTenantId(id);
    if (id === 'optimum') return;          // master workspace holds no records
    setLoadingTenant(true);
    await initStore({ tenantId: id, seedIfEmpty: false });
    store.loadedTenantIds().then(setLoadedTenants).catch(() => setLoadedTenants([]));
    setLoadingTenant(false);
  };

  const open = (id) => { setSelectedId(id); setView('factsheet'); };
  const current = tenants.find((t) => t.id === tenantId);

  // Writing from the master workspace used to silently redirect into the 'nghs'
  // CLIENT workspace — a development shortcut from when nghs was the only tenant
  // with data. With several real clients loaded, and Optimum staff working from
  // master by default, that means a new record lands in a real client's estate
  // with no indication and no undo. Refuse instead: master is a place to look
  // across clients, not a place to author into one.
  const canAuthorHere = tenantId !== 'optimum';

  const createNew = async () => {
    if (!canAuthorHere) return;
    const fs = await store.createFactSheet(tenantId, 'Application', 'New Application');
    open(fs.id);
  };

  const reset = async () => { await store.resetToSeed(); setView('inventory'); setSelectedId(null); };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Every control on this bar sits on the theme-blue AppBar, so nothing
          here may use the page's slate/navy ink colors — grey-on-blue and
          blue-on-blue were both genuinely illegible. White ink, and the
          active (contained) nav pill inverts to white-on-navy-text. */}
      <AppBar position="sticky" elevation={1} sx={{ borderBottom: '3px solid #0D5B9A' }}>
        <Toolbar sx={{
          gap: { xs: 1, lg: 2 }, minWidth: 0,
          // Two rows: identity and context on the first, navigation on the
          // second. minHeight must be released or MUI's fixed Toolbar height
          // clips the wrapped row instead of growing to fit it.
          flexWrap: 'wrap', rowGap: 0.5, minHeight: 'auto !important', py: 1,
        }}>
          {/* Optimum brand wordmark — infinity mark + navy/blue, grey descriptor */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <PrimordiaLogo size={36} />
            <Box sx={{ lineHeight: 1 }}>
              <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: 20, lineHeight: 1, letterSpacing: 0.3 }}>
                Primordi<span style={{ color: '#0D5B9A' }}>a</span>
              </Typography>
              <Typography sx={{ fontSize: 8, color: '#5C6B7A', letterSpacing: 1.4, fontWeight: 700, display: { xs: 'none', lg: 'block' } }}>
                WHERE ENTERPRISE SYSTEMS BEGIN
              </Typography>
              <Typography sx={{ fontSize: 7.5, color: '#9AA6B2', letterSpacing: 0.5, fontWeight: 600, display: { xs: 'none', lg: 'block' } }}>
                by Optimum Healthcare IT
              </Typography>
            </Box>
          </Box>

          {/* White-labelling: the client's own logo in their portal */}
          {current?.logo && current.kind === 'client' && (
            <>
              <Box sx={{ width: '1px', height: 34, bgcolor: 'divider', mx: 1.5, display: { xs: 'none', sm: 'block' } }} />
              <Box component="img" src={current.logo} alt={current.name}
                sx={{ height: 30, maxWidth: { sm: 96, lg: 180 }, objectFit: 'contain', flexShrink: 1, minWidth: 0, display: { xs: 'none', sm: 'block' } }} />
            </>
          )}
          <Box data-spacer="1" sx={{ flex: 1 }} />
          {/* A bare icon is not a label. Collapsed, the ENTIRE navigation hid
              behind an unlabelled hamburger, so the words the product itself uses
              -- Manage, Reports -- appeared nowhere on screen, and someone looking
              for "Manage" reasonably concluded it did not exist. */}
          {/* Collapsed, this used to be ONE menu holding everything: six Explore
              entries, fifteen Reports and seven Manage entries in a single
              scrolling drawer, which put Manage roughly twenty items down. A
              drawer where the thing you want is below the fold is barely better
              than the unlabelled hamburger it replaced.
              Three groups instead, matching the full-width bar's own vocabulary
              so the product reads the same at any width. Three buttons also fit
              at any width worth supporting, which is what stops this being a
              breakpoint that needs retuning every time a screen is added --
              the last two additions ate the headroom the 1400px threshold was
              chosen for. */}
          {compact && (
            <Stack direction="row" spacing={0.5}
              sx={{ flexBasis: '100%', order: 2, flexShrink: 0, flexWrap: 'wrap', rowGap: 0.5 }}>
              <Button onClick={(e) => setExploreAnchor(e.currentTarget)} endIcon={<ExpandMoreIcon />}
                sx={NAV_BTN} variant={EXPLORE_VIEWS.includes(view) ? 'contained' : 'text'}>Explore</Button>
              {current?.kind === 'master' && (
                <Button onClick={(e) => setMasterAnchor(e.currentTarget)} endIcon={<ExpandMoreIcon />}
                  sx={NAV_BTN} variant={MASTER_VIEWS.includes(view) ? 'contained' : 'text'}>Master</Button>
              )}
              {current?.kind !== 'master' && (
                <Button onClick={(e) => setServersAnchor(e.currentTarget)} endIcon={<ExpandMoreIcon />}
                  sx={NAV_BTN} variant={SERVER_VIEWS.includes(view) ? 'contained' : 'text'}>Servers</Button>
              )}
              <Button onClick={(e) => setFinopsAnchor(e.currentTarget)} endIcon={<ExpandMoreIcon />}
                sx={NAV_BTN} variant={FINOPS_VIEWS.includes(view) ? 'contained' : 'text'}>FinOps</Button>
              <Button onClick={(e) => setReportsAnchor(e.currentTarget)} endIcon={<ExpandMoreIcon />}
                sx={NAV_BTN} variant="text">Reports</Button>
              <Button onClick={(e) => setManageAnchor(e.currentTarget)} endIcon={<ExpandMoreIcon />}
                sx={NAV_BTN} variant={MANAGE_VIEWS.includes(view) ? 'contained' : 'text'}>Manage</Button>
            </Stack>
          )}
          {/* Optimum's own shared knowledge, master-only. Both entries here span
              every client on purpose, which is exactly why they are not filed
              with the tools that act on one. */}
          <Menu anchorEl={masterAnchor} open={!!masterAnchor} onClose={() => setMasterAnchor(null)}>
            {[['catalog', 'Master catalog'], ['catalogintel', 'Cross-client app intelligence']].map(([k, label]) => (
              <NavItem key={k} viewKey={k} label={label} selected={view === k}
                onPick={(v) => { setView(v); setMasterAnchor(null); }} />
            ))}
          </Menu>
          {/* Server tools. Hidden entirely in master: every one of these acts on
              a client's server inventory, and master holds none. */}
          <Menu anchorEl={serversAnchor} open={!!serversAnchor} onClose={() => setServersAnchor(null)}>
            {[['servers', 'Server inventory'], ['naming', 'Naming dictionary'],
              ['servermap', 'Server \u2192 deployment mapping'],
              ['infra', 'Infrastructure import']].map(([k, label]) => (
                <NavItem key={k} viewKey={k} label={label} selected={view === k}
                  onPick={(v) => { setView(v); setServersAnchor(null); }} />
              ))}
          </Menu>
          <Menu anchorEl={finopsAnchor} open={!!finopsAnchor} onClose={() => setFinopsAnchor(null)}>
            {[['finops', 'Cloud spend & showback'], ['clouddiscovery', 'Cloud discovery (what runs)'],
              ['rightsizing', 'Right-sizing'], ['cost', 'Cost modelling']].map(([k, label]) => (
                <NavItem key={k} viewKey={k} label={label} selected={view === k}
                  onPick={(v) => { setView(v); setFinopsAnchor(null); }} />
              ))}
          </Menu>
          <Menu anchorEl={exploreAnchor} open={!!exploreAnchor} onClose={() => setExploreAnchor(null)}>
            {/* Servers moved to its own group; leaving it here too would put
                the same screen in two menus and light two nav buttons at once,
                which is the drift this file has already paid for. */}
            {[...(current?.kind === 'master' ? [] : [['inventory', 'Applications'], ['deployments', 'Deployments']]),
              ...(current?.kind === 'master' ? [['allrecords', 'All client records']] : []),
              ['dashboard', 'Dashboards'],
              ['mydash', 'My dashboard'], ...(canSeeRoadmap ? [['whatsnew', "What's coming"]] : []),
              ['help', 'Help center']].map(([k, label]) => (
                <NavItem key={k} viewKey={k} label={label} selected={view === k}
                  onPick={(v) => { setView(v); setExploreAnchor(null); }} />
              ))}
          </Menu>
          <Box sx={{
            display: compact ? 'none' : 'flex', alignItems: 'center', gap: 2,
            flexBasis: '100%', order: 2, flexWrap: 'wrap', rowGap: 0.5,
          }}>
          <Button startIcon={<ListAltIcon />} onClick={() => setView('servers')} sx={NAV_BTN}
            variant={view === 'servers' ? 'contained' : 'text'}>Servers</Button>
          <Button data-tour="nav-inventory" startIcon={<ListAltIcon />} onClick={() => setView('inventory')} sx={NAV_BTN}
            variant={view === 'inventory' ? 'contained' : 'text'}>Applications</Button>
          <Button startIcon={<DashboardIcon />} onClick={() => setView('dashboard')} sx={NAV_BTN}
            variant={view === 'dashboard' ? 'contained' : 'text'}>Dashboards</Button>
          {/* The user-composable dashboard sits beside the fixed one — the
              Dashboards area is a single view here, not a menu, so a sibling
              entry fits the existing pattern better than a nested toggle. */}
          <Button startIcon={<DashboardCustomizeIcon />} onClick={() => setView('mydash')} sx={NAV_BTN}
            variant={view === 'mydash' ? 'contained' : 'text'}>My dashboard</Button>

          {/* Grouped menus — a flat button per view stopped scaling once the
              reporting and admin screens landed. */}
          {current?.kind === 'master' && (
            <Button endIcon={<ExpandMoreIcon />} onClick={(e) => setMasterAnchor(e.currentTarget)} sx={NAV_BTN}
              variant={MASTER_VIEWS.includes(view) ? 'contained' : 'text'}>Master</Button>
          )}
          {current?.kind !== 'master' && (
            <Button endIcon={<ExpandMoreIcon />} onClick={(e) => setServersAnchor(e.currentTarget)} sx={NAV_BTN}
              variant={SERVER_VIEWS.includes(view) ? 'contained' : 'text'}>Servers</Button>
          )}
          <Button endIcon={<ExpandMoreIcon />} onClick={(e) => setReportsAnchor(e.currentTarget)} sx={NAV_BTN}
            variant={REPORT_VIEWS.includes(view) ? 'contained' : 'text'}>Reports</Button>
          <Menu anchorEl={reportsAnchor} open={!!reportsAnchor} onClose={() => setReportsAnchor(null)}>
            {[['gallery', 'All reports (gallery)'], ['landscape', 'Landscape heat map'], ['capabilitymap', 'Business capability map'], ['matrix', 'Matrix & circle map'],
              ['roadmap', 'Migration roadmap'], ['mindmap', 'Relationship mind map'],
              ['relationmap', 'Relation map (printable)'],
              ['portfolio', 'Portfolio & radar'],
              ['obsolescence', 'Obsolescence & technology risk'],
              ['dataflow', 'Data flow diagram'],
              ['targetarch', 'Target architecture (Epic CSG)'],
              ['readiness', 'Cutover readiness & gaps'], ['interfaces', 'Interface engines'],
              ['views', 'Saved views & reports'], ['snapshots', 'Progress since kickoff'], ['benchmarks', 'Cross-client benchmarks'], ['waveplanner', 'Wave planner (dependency-ordered)']].map(([k, label]) => (
              <MenuItem key={k} selected={view === k}
                onClick={() => { setView(k); setReportsAnchor(null); }}>{label}</MenuItem>
            ))}
          </Menu>

          {canSeeRoadmap && (
            <Button size="small" onClick={() => setView('whatsnew')}
              variant={view === 'whatsnew' ? 'contained' : 'text'}
              sx={{ ...NAV_BTN, textTransform: 'none', fontSize: 12 }}>What&rsquo;s coming</Button>
          )}

          <Button endIcon={<ExpandMoreIcon />} onClick={(e) => setFinopsAnchor(e.currentTarget)} sx={NAV_BTN}
            variant={FINOPS_VIEWS.includes(view) ? 'contained' : 'text'}>FinOps</Button>
          <Button data-tour="nav-manage" endIcon={<ExpandMoreIcon />} onClick={(e) => setManageAnchor(e.currentTarget)} sx={NAV_BTN}
            variant={MANAGE_VIEWS.includes(view) ? 'contained' : 'text'}>Manage</Button>
          <Menu anchorEl={manageAnchor} open={!!manageAnchor} onClose={() => setManageAnchor(null)}>
            {/* One entry, because users, keys, values and clients are one job:
                deciding what people may do. They were four unrelated lines in a
                menu of twenty. */}
            <MenuItem selected={view === 'admin'} onClick={() => { setView('admin'); setManageAnchor(null); }}>
              Administration
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setBulkOpen(true); setManageAnchor(null); }}>Bulk edit &amp; duplicates</MenuItem>
            {current?.kind !== 'master' && (<MenuItem selected={view === 'surveys'} onClick={() => { setView('surveys'); setManageAnchor(null); }}>Discovery surveys</MenuItem>)}
            {current?.kind !== 'master' && (<MenuItem selected={view === 'extract'} onClick={() => { setView('extract'); setManageAnchor(null); }}>Extract from document</MenuItem>)}
            {current?.kind !== 'master' && (<MenuItem selected={view === 'relwizard'} onClick={() => { setView('relwizard'); setManageAnchor(null); }}>Relationship wizard</MenuItem>)}
            {current?.kind !== 'master' && (<MenuItem selected={view === 'csg'} onClick={() => { setView('csg'); setManageAnchor(null); }}>CSG target state (Epic)</MenuItem>)}
            <MenuItem selected={view === 'meetings'} onClick={() => { setView('meetings'); setManageAnchor(null); }}>Meeting transcripts</MenuItem>
            {current?.kind !== 'master' && (<MenuItem selected={view === 'templates'} onClick={() => { setView('templates'); setManageAnchor(null); }}>Template library</MenuItem>)}
            {current?.kind !== 'master' && (<MenuItem selected={view === 'ai'} onClick={() => { setView('ai'); setManageAnchor(null); }}>AI enrichment</MenuItem>)}
            {current?.kind !== 'master' && (<MenuItem selected={view === 'forms'} onClick={() => { setView('forms'); setManageAnchor(null); }}>Discovery forms (Excel)</MenuItem>)}
            <MenuItem selected={view === 'files'} onClick={() => { setView('files'); setManageAnchor(null); }}>Documents</MenuItem>
            {current?.kind !== 'master' && (<MenuItem selected={view === 'contracts'} onClick={() => { setView('contracts'); setManageAnchor(null); }}>Contracts &amp; renewals</MenuItem>)}
          </Menu>
          </Box>
          <ThemeModeToggle />

          {/* Tenant / client portal switcher */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: { xs: 0, md: 2 }, minWidth: 0, flexShrink: 1 }}>
            <Select data-tour="workspace-switcher" aria-label="Workspace switcher" size="small" value={tenantId} onChange={(e) => { switchTenant(e.target.value); setView('inventory'); }}
              sx={{ minWidth: { xs: 130, md: 180 }, maxWidth: 280, flexShrink: 1, bgcolor: 'action.hover' }}
              renderValue={(id) => {
                const t = tenants.find((x) => x.id === id);
                return (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {t?.logo ? (
                      <img src={t.logo} alt="" style={{ height: 18, maxWidth: 60, objectFit: 'contain' }} />
                    ) : (
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t?.color }} />
                    )}
                    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t?.name}</Box>
                    {t?.kind === 'master' && <Chip size="small" label="MASTER" sx={{ height: 16, fontSize: 9, bgcolor: '#021F4B', color: '#fff' }} />}
                    {t?.status === 'archived' && <Chip size="small" label="ARCHIVED" sx={{ height: 16, fontSize: 9, bgcolor: '#9E9E9E', color: '#fff' }} />}
                  </Stack>
                );
              }}
            >
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.color }} />
                    <span>{t.name}</span>
                    {t.kind === 'master' && <Chip size="small" label="MASTER" sx={{ height: 16, fontSize: 9 }} />}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </Stack>
          {/* Top right of EVERY screen, and not behind any role. The people who
              hit a broken screen are mostly not the people who administer it,
              so a feedback control only admins can reach collects nothing. */}
          <Tooltip title="Help center">
            <IconButton size="small" onClick={() => { setHelpTopic(null); setView('help'); }}
              sx={{ ml: 1, color: 'inherit', opacity: 0.8 }} data-tour="help-center-icon" aria-label="Open help center">
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Report a problem or suggest something">
            <IconButton size="small" onClick={() => setFeedbackOpen(true)}
              sx={{ ml: 1, color: 'inherit', opacity: 0.8 }} data-tour="feedback-icon" aria-label="Send feedback">
              <FeedbackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Beside the account, not adrift in the middle of the bar. Someone who
              is lost looks where they identify themselves. */}
          <Tooltip title="Restart the guided tour">
            <IconButton size="small" onClick={requestTourReplay} aria-label="Restart the guided tour"
              sx={{ ml: 1, color: 'inherit', opacity: 0.8 }}>
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {session ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1, minWidth: 0, flexShrink: 1 }}>
              {/* minWidth:0 + noWrap: the email TRUNCATES rather than pushing
                  the Account button (Change password / Sign out) off-screen —
                  which is exactly what a long address used to do. */}
              <Box sx={{ textAlign: 'right', lineHeight: 1.1, display: { xs: 'none', md: 'block' }, minWidth: 0, maxWidth: 170 }}>
                <Typography noWrap sx={{ fontSize: 11, fontWeight: 700, color: 'inherit' }}>{session.email}</Typography>
                <Typography noWrap sx={{ fontSize: 9, color: 'inherit', opacity: 0.75 }}>
                  {(session.groups || []).includes('OptimumAdmins') ? 'Optimum admin' : (session.tenantName || session.tenantId || 'client user')}
                </Typography>
              </Box>
              <Button size="small" startIcon={<LogoutIcon />} data-tour="account-menu"
                sx={{ color: 'inherit', opacity: 0.8, flexShrink: 0, whiteSpace: 'nowrap' }}
                onClick={(e) => setAccountAnchor(e.currentTarget)}>
                Account
              </Button>
              <Menu anchorEl={accountAnchor} open={!!accountAnchor} onClose={() => setAccountAnchor(null)}>
                <MenuItem onClick={() => { setChangePwOpen(true); setAccountAnchor(null); }}>
                  Change password
                </MenuItem>
                <MenuItem onClick={() => { setAuthAppOpen(true); setAccountAnchor(null); }}>
                  Authenticator app
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setAccountAnchor(null); signOut(); setSession(null); }}>
                  Sign out
                </MenuItem>
              </Menu>
            </Stack>
          ) : (
            <Button size="small" startIcon={<RestartAltIcon />} onClick={reset} sx={{ color: 'inherit', opacity: 0.75 }}>Reset</Button>
          )}
        </Toolbar>
      </AppBar>

      {current?.kind === 'master' && (
        <Box sx={{ bgcolor: '#021F4B', color: '#fff', px: 3, py: 0.5, fontSize: 13 }}>
          {/* This said "across all client portals", which is a promise the app
              cannot keep: workspaces load ONE at a time on purpose — an unscoped
              fetch of every client exceeds the response cap and used to fail
              into seed data — so this view is the union of whatever has been
              loaded this session. Naming them is the same fix C1 and M4 got:
              say what the number actually covers. */}
          Master workspace — showing the {loadedTenants.length} workspace
          {loadedTenants.length === 1 ? '' : 's'} loaded so far
          {loadedTenants.length ? <> (<b>{loadedTenants.join(', ')}</b>)</> : null}, not every client.
          Switch the selector to a client to load theirs.
        </Box>
      )}

      {/* The right-hand nav Drawer lived here. It became unreachable when the
          collapsed bar was split into Explore / FinOps / Reports / Manage --
          nothing calls setNavOpen any more. Removed rather than left in place:
          it carried its own copy of the nav lists, so a dead drawer would have
          quietly disagreed with the live menus the first time a screen moved. */}

      {/* "Applications" means two different things depending on where you are
          standing, and conflating them was the bug. In a CLIENT workspace it is
          that client's application records. In the MASTER workspace it is the
          product catalogue -- because master has no application estate of its
          own, and what it was showing instead was every client's fact sheets
          blended together, complete with their named business and technical
          owners. Someone opening "the master list" to see clean product data
          got Greg Coffman at Dana-Farber.
          The blend still exists and is still useful; it is now reached through
          "All client records" and named as what it is. */}
      {view === 'inventory' && current?.kind === 'master'
        && <MasterCatalog onOpenInstance={open} />}
      {view === 'inventory' && current?.kind !== 'master'
        && <Inventory tenantId={tenantId} onOpen={open} onCreate={createNew} initialType="Application" />}
      {view === 'allrecords' && current?.kind === 'master'
        && <Inventory tenantId={tenantId} onOpen={open} onCreate={createNew} initialType="Application" />}
      {view === 'servers' && <Inventory tenantId={tenantId} onOpen={open} onCreate={createNew} initialType="ITComponent" />}
      {/* Deployments had no list of their own, which made them the one entity
          you could only reach through something else -- yet they are where
          version, environment, servers and cutover date actually live, and
          Server mapping counts them on its own header. Inventory already takes
          a type, so the screen existed; nothing pointed at it. */}
      {view === 'deployments' && <Inventory tenantId={tenantId} onOpen={open} onCreate={createNew} initialType="Deployment" />}
      {/* A master sitting in the optimum workspace gets the cross-client
          overview — optimum deliberately holds no records, and the per-
          workspace Dashboard rendered it as a blank product. Entering any
          client portal restores the ordinary Dashboard unchanged. */}
      {view === 'dashboard' && (tenantId === 'optimum' && current?.kind === 'master'
        ? <MasterDashboard onOpenTenant={(id) => switchTenant(id)} onNavigate={setView} />
        : <Dashboard tenantId={tenantId} />)}
      {/* Works in BOTH master and client workspaces — in master it groups its
          warnings per client, mirroring MasterDashboard's callbacks so a
          warning can enter the client's workspace and land on the right
          screen. */}
      {view === 'mydash' && (
        <MyDashboard tenantId={tenantId} session={session}
          onNavigate={setView} onOpenTenant={(id) => switchTenant(id)} />
      )}
      {view === 'landscape' && <LandscapeReport tenantId={tenantId} onOpen={open} />}
      {view === 'capabilitymap' && <CapabilityMap tenantId={tenantId} onOpen={open} />}
      {current?.kind === 'master' && isClientOnly(view) && (
        <Box sx={{ p: 3, maxWidth: 720 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Switch to a client workspace</Typography>
          <Typography variant="body2" color="text.secondary">
            This tool works on one client&rsquo;s estate. The master workspace holds the shared
            product catalogue rather than any client&rsquo;s applications or servers, so there is
            nothing here for it to act on. Pick a client from the workspace selector above.
          </Typography>
        </Box>
      )}
      {view === 'catalog' && current?.kind === 'master' && <MasterCatalog onOpenInstance={open} />}
      {view === 'admin' && (
        <AdminPortal tenants={tenants} tenantId={tenantId}
          onTenantsChanged={() => store.listTenants().then(setTenants)} />
      )}
      {/* The old direct routes stay reachable — deep links and anything that
          still sets these views keep working, they are just no longer offered
          in the menu. */}
      {view === 'users' && <UserAdmin tenants={tenants} />}
      {view === 'matrix' && <MatrixReport tenantId={tenantId} onOpen={open} />}
      {view === 'roadmap' && <RoadmapView tenantId={tenantId} onOpen={open} />}
      {view === 'mindmap' && <MindMap tenantId={tenantId} onOpen={open} />}
      {view === 'lists' && <ListsAdmin tenantId={tenantId} />}
      {view === 'readiness' && <ReadinessView tenantId={tenantId} onOpen={open} />}
      {view === 'interfaces' && <InterfaceHub tenantId={tenantId} onOpen={open} />}
      {view === 'cost' && <CostModel tenantId={tenantId} />}
      {view === 'surveys' && <SurveyWorkflow tenantId={tenantId} />}
      {view === 'files' && <FileExchange tenantId={tenantId} />}
      {view === 'contracts' && <ContractsRenewals tenantId={tenantId} />}
      {view === 'finops' && <FinOps tenantId={tenantId} />}
      {view === 'clouddiscovery' && <CloudDiscovery tenantId={tenantId} />}
      {view === 'forms' && <FormsRoundtrip tenantId={tenantId} />}
      {view === 'views' && <SavedViews tenantId={tenantId} onOpen={open} />}
      {view === 'snapshots' && <SnapshotCompare tenantId={tenantId} onOpen={open} />}
      {view === 'benchmarks' && <Benchmarks tenantId={tenantId} />}
      {view === 'waveplanner' && <WavePlanner tenantId={tenantId} onOpen={open} />}
      {view === 'extract' && <DocumentExtract tenantId={tenantId} />}
      {view === 'infra' && <InfrastructureImport tenantId={tenantId} />}
      {view === 'relwizard' && <RelationshipWizard tenantId={tenantId} onOpen={open} />}
      {view === 'csg' && <CsgTargetState tenantId={tenantId} />}
      {view === 'servermap' && <ServerMapping tenantId={tenantId} onOpen={open} onNavigate={setView} />}
      {view === 'rightsizing' && <Rightsizing tenantId={tenantId} />}
      {view === 'templates' && <TemplateLibrary tenantId={tenantId} />}
      {view === 'relationmap' && <RelationMap tenantId={tenantId} onOpen={open} />}
      {view === 'portfolio' && <PortfolioReport tenantId={tenantId} onOpen={open} />}
      {view === 'obsolescence' && <Obsolescence tenantId={tenantId} />}
      {view === 'dataflow' && <DataFlowDiagram tenantId={tenantId} onOpen={open} />}
      {view === 'naming' && <NamingDictionary tenantId={tenantId} />}
      {view === 'meetings' && <MeetingIntelligence tenantId={tenantId} onOpen={open} />}
      {view === 'targetarch' && <TargetArchitecture tenantId={tenantId} />}
      {/* Visible to every signed-in user, client or Optimum — the content is
          written to be safe for anyone with a login to read. */}
      {view === 'whatsnew' && canSeeRoadmap && (
        <Roadmap onRequest={() => { setFeedbackKind('idea'); setFeedbackOpen(true); }} />
      )}
      {/* The gallery launches a report by view key. Saved configuration is
          carried but most reports do not yet accept one, so they open at their
          own default — the gallery says so rather than implying it applied. */}
      {view === 'gallery' && <ReportsGallery tenantId={tenantId} onOpenView={(k) => k && setView(k)} />}
      {/* Master-only: shows NAMED client data, unlike the anonymised Benchmarks
          screen. The component gates itself too — this is defence in depth. */}
      {view === 'catalogintel' && current?.kind === 'master' && <CatalogIntelligence tenantId={tenantId} />}
      {view === 'ai' && <AiEnrichment tenantId={tenantId} />}
      {view === 'customers' && current?.kind === 'master' && <CustomerAdmin onChanged={() => store.listTenants().then(setTenants)} />}
      {/* Bulk edit is a WRITE tool, so it must not open against master and
          redirect into a client. Only mount it in a workspace we may author in. */}
      {canAuthorHere && (
        <BulkTools open={bulkOpen} onClose={() => setBulkOpen(false)} tenantId={tenantId} onChanged={() => setView('inventory')} />
      )}
      {/* Not gated on canAuthorHere: changing your own password is not authoring
          in this workspace, and a viewer — or anyone sitting in the master
          workspace — must still be able to do it. */}
      <ChangePassword open={changePwOpen} onClose={() => setChangePwOpen(false)} />
      <AuthenticatorSettings
        open={authAppOpen || mfaRequired}
        required={mfaRequired}
        onSignOut={() => { signOut(); setSession(null); setMfaRequired(false); }}
        onClose={() => { setAuthAppOpen(false); setMfaRequired(false); }}
      />
      {/* `screen` and `workspace` are what make a report reproducible without
          going back to the reporter, so they are passed rather than described. */}
      {storageWarning && (
        <Alert severity="warning" onClose={() => setStorageWarning(null)} sx={{ borderRadius: 0 }}>
          {storageWarning}
        </Alert>
      )}
      <Tour
        steps={tourStepsForSession(session)}
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onFinish={() => setTourOpen(false)}
      />
      <Feedback open={feedbackOpen} onClose={() => { setFeedbackOpen(false); setFeedbackKind(null); }}
        screen={view} workspace={tenantId} initialKind={feedbackKind} />
      {view === 'help' && <HelpCenter initialTopic={helpTopic} />}
      {view === 'factsheet' && selectedId && (
        <FactSheetDetail id={selectedId} tenantId={tenantId} onBack={() => setView('inventory')} onOpen={open} />
      )}
    </Box>
  );
}
